import React, { useEffect, useId, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { GlassPanel, monoLabelSx, STUDIO_ACCENT } from '../shared'

const Sheet = styled(GlassPanel)({
  position: 'absolute',
  left: '50%',
  bottom: 88,
  transform: 'translateX(-50%)',
  width: 'min(340px, calc(100vw - 32px))',
  zIndex: 40,
  padding: '12px 12px 11px',
  borderRadius: 12,
  boxShadow: '0 22px 48px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,207,125,0.18)',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  animation: 'pinSheetIn 160ms ease-out',
  '@keyframes pinSheetIn': {
    from: { opacity: 0, transform: 'translateX(-50%) translateY(8px)' },
    to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
  },
})

const SheetHead = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 8,
})

const SheetKind = styled(Box)({
  ...monoLabelSx,
  fontSize: 7.5,
  color: STUDIO_ACCENT.gold,
  marginBottom: 3,
})

const SheetTitle = styled(Typography)({
  fontFamily: 'var(--ds-font)',
  fontSize: 13.5,
  fontWeight: 680,
  letterSpacing: '-0.015em',
  color: 'var(--ds-night-ink)',
  lineHeight: 1.2,
})

const CloseBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 26,
  height: 26,
  borderRadius: 7,
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
  color: 'var(--ds-night-ink-subtle)',
  background: 'rgba(244,243,246,0.05)',
  border: '1px solid var(--ds-night-border)',
  fontSize: 14,
  lineHeight: 1,
  flexShrink: 0,
  transition: 'color 120ms ease, border-color 120ms ease',
  '&:hover': { color: 'var(--ds-night-ink)', borderColor: 'rgba(244,243,246,0.28)' },
})

const NoteArea = styled('textarea')({
  width: '100%',
  minHeight: 72,
  maxHeight: 140,
  resize: 'vertical',
  boxSizing: 'border-box',
  padding: '9px 10px',
  borderRadius: 8,
  border: '1px solid var(--ds-night-border)',
  background: 'rgba(244,243,246,0.04)',
  color: 'var(--ds-night-ink)',
  fontFamily: 'var(--ds-font)',
  fontSize: 12.5,
  lineHeight: 1.45,
  outline: 'none',
  transition: 'border-color 120ms ease',
  '&:focus': { borderColor: 'rgba(255,207,125,0.55)' },
  '&::placeholder': { color: 'var(--ds-night-ink-subtle)' },
})

const Meta = styled(Typography)({
  fontSize: 10.5,
  lineHeight: 1.4,
  color: 'var(--ds-night-ink-subtle)',
  marginTop: -4,
})

const Actions = styled(Box)({
  display: 'flex',
  gap: 6,
  alignItems: 'center',
})

const GhostBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  height: 28,
  padding: '0 11px',
  borderRadius: 7,
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--ds-night-ink-subtle)',
  border: '1px solid var(--ds-night-border)',
  background: 'rgba(244,243,246,0.03)',
  transition: 'color 120ms ease, border-color 120ms ease',
  '&:hover': { color: 'var(--ds-night-ink)', borderColor: 'rgba(244,243,246,0.28)' },
})

const DangerBtn = styled(GhostBtn)({
  color: '#f2795f',
  borderColor: 'rgba(242,121,95,0.35)',
  '&:hover': { color: '#ff9a86', borderColor: 'rgba(242,121,95,0.55)' },
})

const ApplyBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  height: 28,
  padding: '0 14px',
  borderRadius: 7,
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 700,
  color: '#0a0912',
  background: STUDIO_ACCENT.gold,
  border: '1px solid transparent',
  marginLeft: 'auto',
  transition: 'filter 120ms ease',
  '&:hover': { filter: 'brightness(1.06)' },
  '&:disabled': { opacity: 0.45, cursor: 'default', filter: 'none' },
})

export interface PinComposeSheetProps {
  mode: 'create' | 'edit'
  initialText: string
  /** Optional print name shown under the title. */
  printLabel?: string | null
  onApply: (text: string) => void
  onCancel: () => void
  onDelete?: () => void
}

export function PinComposeSheet({
  mode,
  initialText,
  printLabel,
  onApply,
  onCancel,
  onDelete,
}: PinComposeSheetProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const areaRef = useRef<HTMLTextAreaElement>(null)
  const [text, setText] = useState(initialText)
  const trimmed = text.trim()
  const canApply = trimmed.length > 0

  useEffect(() => {
    setText(initialText)
  }, [initialText])

  useEffect(() => {
    const t = window.setTimeout(() => areaRef.current?.focus(), 40)
    return () => window.clearTimeout(t)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  return (
    <Sheet
      ref={panelRef}
      role='dialog'
      aria-modal='true'
      aria-labelledby={titleId}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <SheetHead>
        <Box>
          <SheetKind>STORY · PIN</SheetKind>
          <SheetTitle id={titleId}>{mode === 'create' ? 'Pin a note' : 'Edit note'}</SheetTitle>
        </Box>
        <CloseBtn type='button' aria-label='Close' onClick={onCancel}>
          ×
        </CloseBtn>
      </SheetHead>

      {printLabel && <Meta>Beside {printLabel}</Meta>}

      <NoteArea
        ref={areaRef}
        value={text}
        placeholder='What should guests find here?'
        maxLength={280}
        spellCheck
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canApply) {
            e.preventDefault()
            onApply(trimmed)
          }
        }}
      />

      <Actions>
        {mode === 'edit' && onDelete && (
          <DangerBtn type='button' onClick={onDelete}>
            Delete
          </DangerBtn>
        )}
        <GhostBtn type='button' onClick={onCancel}>
          Cancel
        </GhostBtn>
        <ApplyBtn type='button' disabled={!canApply} onClick={() => onApply(trimmed)}>
          {mode === 'create' ? 'Pin' : 'Save'}
        </ApplyBtn>
      </Actions>
    </Sheet>
  )
}
